import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Plus, Trash2, ArrowUp, ArrowDown, Pencil, Users as UsersIcon, X, Youtube } from 'lucide-react';
import { learningApi, usersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { mins } from './MyLearning';

/**
 * Courses (admin): create a course, add its Tally features — each a YouTube
 * link — in learning order, and assign it to everyone, a team or chosen
 * people, with an optional due date.
 */
const LearningAdmin: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const [courses, setCourses] = useState<any[]>([]);
  const [sel, setSel] = useState<any | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [feat, setFeat] = useState<{ id?: number; title: string; youtube_url: string; note: string } | null>(null);
  const [asg, setAsg] = useState<{ scope: string; team: string; user_ids: string[]; due_date: string }>({ scope: 'all', team: 'Service', user_ids: [], due_date: '' });
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    try { setCourses((await learningApi.courses()).data || []); } catch (e: any) { showError('Could not load courses', e?.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadList(); usersApi.getBasic().then(r => setUsers(r.data || [])).catch(() => {}); }, [loadList]);
  const open = async (id: number) => { try { setSel((await learningApi.adminCourse(id)).data); setFeat(null); } catch (e: any) { showError('Could not open', e?.message); } };

  const run = async (fn: () => Promise<any>, ok?: string) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.id) setSel(r.data); if (ok) showSuccess(ok, ''); loadList(); return r; }
    catch (e: any) { showError('Could not save', e?.message); }
    finally { setBusy(false); }
  };

  const newCourse = () => setSel({ id: 0, title: '', description: '', is_active: 1, features: [], assignments: [] });
  const saveCourse = () => run(() => learningApi.saveCourse({ id: sel.id || undefined, title: sel.title, description: sel.description, is_active: !!sel.is_active }), 'Course saved');
  const saveFeature = async () => {
    const r = await run(() => learningApi.saveFeature(sel.id, feat), feat?.id ? 'Feature updated' : 'Feature added');
    if (r) { setFeat(null); if (r.data && r.data.embeddable === false) showError('Check the video', 'YouTube did not confirm this video — make sure it is public or unlisted and allows embedding.'); }
  };
  const move = (i: number, d: number) => {
    const ids = sel.features.map((f: any) => f.id);
    const j = i + d; if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run(() => learningApi.reorder(sel.id, ids));
  };
  const assignLabel = (a: any) => a.scope === 'all' ? 'Everyone' : a.scope === 'team' ? `Team: ${a.team}` : (a.user_name || a.user_id);

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="max-w-6xl mx-auto grid gap-3 lg:grid-cols-[300px_1fr]">
        <div className="bg-white rounded-lg shadow p-3 h-fit">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            <h1 className="font-semibold text-gray-800 flex-1">Courses</h1>
            <button onClick={newCourse} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"><Plus className="h-3.5 w-3.5" /> New</button>
          </div>
          {courses.length === 0 && <div className="text-sm text-gray-400 py-4 text-center">No courses yet.</div>}
          {courses.map(c => (
            <button key={c.id} onClick={() => open(c.id)} className={`w-full text-left px-2 py-2 rounded text-sm ${sel?.id === c.id ? 'bg-blue-50' : 'hover:bg-gray-50'} ${c.is_active ? '' : 'opacity-50'}`}>
              <div className="font-medium text-gray-800">{c.title}{c.is_active ? '' : ' (off)'}</div>
              <div className="text-[11px] text-gray-500">{c.features} feature(s){Number(c.total_seconds) ? ` · ${mins(c.total_seconds)}` : ''}</div>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          {!sel ? (
            <div className="text-sm text-gray-400 py-10 text-center">Pick a course, or create a new one.</div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-2">
                <input value={sel.title} onChange={e => setSel({ ...sel, title: e.target.value })} placeholder="Course title, e.g. Tally GST" className="px-3 py-2 border border-gray-300 rounded text-base font-semibold" />
                <textarea value={sel.description || ''} onChange={e => setSel({ ...sel, description: e.target.value })} placeholder="What this course covers (optional)" rows={2} className="px-3 py-2 border border-gray-300 rounded text-sm" />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={!!sel.is_active} onChange={e => setSel({ ...sel, is_active: e.target.checked ? 1 : 0 })} /> Active (staff can see it)</label>
                  <span className="flex-1" />
                  {sel.id ? <button onClick={() => { if (window.confirm('Remove this course? If staff have already watched it, it is switched off instead, keeping their record.')) run(async () => { const r = await learningApi.deleteCourse(sel.id); setSel(null); return r; }, 'Done'); }} className="text-xs text-red-600 hover:underline">Remove course</button> : null}
                  <button onClick={saveCourse} disabled={busy || !sel.title?.trim()} className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{sel.id ? 'Save' : 'Create course'}</button>
                </div>
              </div>

              {sel.id ? (
                <>
                  {/* Features */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Youtube className="h-4 w-4 text-red-600" />
                      <span className="text-[11px] font-bold uppercase text-gray-500 flex-1">Features, in learning order</span>
                      <button onClick={() => setFeat({ title: '', youtube_url: '', note: '' })} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"><Plus className="h-3.5 w-3.5" /> Add feature</button>
                    </div>
                    {feat && (
                      <div className="border border-blue-200 bg-blue-50/40 rounded p-3 mb-2 grid gap-2">
                        <input value={feat.youtube_url} onChange={e => setFeat({ ...feat, youtube_url: e.target.value })} placeholder="YouTube link — https://youtu.be/…  or  youtube.com/watch?v=…" className="px-2 py-1.5 border border-gray-300 rounded text-sm" />
                        <input value={feat.title} onChange={e => setFeat({ ...feat, title: e.target.value })} placeholder="Feature title — leave blank to use the video's own title" className="px-2 py-1.5 border border-gray-300 rounded text-sm" />
                        <textarea value={feat.note} onChange={e => setFeat({ ...feat, note: e.target.value })} placeholder="Note shown under the video (optional)" rows={2} className="px-2 py-1.5 border border-gray-300 rounded text-sm" />
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setFeat(null)} className="px-3 py-1.5 text-sm rounded border border-gray-300">Cancel</button>
                          <button onClick={saveFeature} disabled={busy || !feat.youtube_url.trim()} className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50">{feat.id ? 'Save feature' : 'Add feature'}</button>
                        </div>
                      </div>
                    )}
                    {sel.features.length === 0 ? <div className="text-sm text-gray-400 border border-dashed rounded p-3">No features yet — add the first YouTube video.</div> : (
                      <div className="border border-gray-200 rounded divide-y divide-gray-100">
                        {sel.features.map((f: any, i: number) => (
                          <div key={f.id} className="flex items-center gap-2 px-2 py-2 text-sm">
                            <span className="w-6 text-right text-gray-400 tabular-nums">{i + 1}.</span>
                            <img src={`https://i.ytimg.com/vi/${f.youtube_id}/default.jpg`} alt="" className="w-16 h-9 object-cover rounded" />
                            <span className="flex-1">
                              <span className="text-gray-800">{f.title}</span>
                              <span className="block text-[11px] text-gray-400">{f.duration_sec ? mins(f.duration_sec) : 'length known after the first play'} · {f.youtube_id}</span>
                            </span>
                            <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-gray-500 disabled:opacity-30" title="Up"><ArrowUp className="h-4 w-4" /></button>
                            <button onClick={() => move(i, 1)} disabled={i === sel.features.length - 1} className="p-1 text-gray-500 disabled:opacity-30" title="Down"><ArrowDown className="h-4 w-4" /></button>
                            <button onClick={() => setFeat({ id: f.id, title: f.title, youtube_url: f.youtube_url || f.youtube_id, note: f.note || '' })} className="p-1 text-gray-500" title="Edit"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => { if (window.confirm(`Remove "${f.title}"?`)) run(() => learningApi.deleteFeature(sel.id, f.id)); }} className="p-1 text-red-500" title="Remove"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Assignment */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <UsersIcon className="h-4 w-4 text-gray-500" />
                      <span className="text-[11px] font-bold uppercase text-gray-500">Who learns this course</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {sel.assignments.length === 0 && <span className="text-sm text-gray-400">Not assigned yet.</span>}
                      {sel.assignments.map((a: any) => (
                        <span key={a.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">
                          {assignLabel(a)}{a.due_date ? ` · due ${String(a.due_date).slice(0, 10).split('-').reverse().join('-')}` : ''}
                          <button onClick={() => run(() => learningApi.unassign(sel.id, a.id))} className="text-gray-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                    <div className="border border-gray-200 rounded p-3 grid gap-2 text-sm">
                      <div className="flex flex-wrap items-center gap-3">
                        {[['all', 'Everyone'], ['team', 'A team'], ['user', 'Chosen people']].map(([v, l]) => (
                          <label key={v} className="flex items-center gap-1"><input type="radio" checked={asg.scope === v} onChange={() => setAsg({ ...asg, scope: v })} /> {l}</label>
                        ))}
                        {asg.scope === 'team' && (
                          <select value={asg.team} onChange={e => setAsg({ ...asg, team: e.target.value })} className="px-2 py-1 border border-gray-300 rounded">
                            <option>Service</option><option>Marketing</option><option>Both</option>
                          </select>
                        )}
                        <label className="flex items-center gap-1 text-gray-600">Due <input type="date" value={asg.due_date} onChange={e => setAsg({ ...asg, due_date: e.target.value })} className="px-2 py-1 border border-gray-300 rounded" /></label>
                      </div>
                      {asg.scope === 'user' && (
                        <div className="max-h-40 overflow-auto border border-gray-100 rounded p-2 grid sm:grid-cols-2 gap-1">
                          {users.map(u => (
                            <label key={u.id} className="flex items-center gap-1.5 text-gray-700">
                              <input type="checkbox" checked={asg.user_ids.includes(u.id)} onChange={e => setAsg({ ...asg, user_ids: e.target.checked ? [...asg.user_ids, u.id] : asg.user_ids.filter(x => x !== u.id) })} /> {u.name}
                            </label>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-end">
                        <button disabled={busy || (asg.scope === 'user' && !asg.user_ids.length)}
                          onClick={async () => { const r = await run(() => learningApi.assign(sel.id, asg), 'Assigned'); if (r) setAsg({ ...asg, user_ids: [] }); }}
                          className="px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50">Assign</button>
                      </div>
                      <p className="text-[11px] text-gray-400">A team is the user's Resource Type in the user master. "Both" covers Service and Marketing staff.</p>
                    </div>
                  </div>
                </>
              ) : <p className="text-sm text-gray-500">Create the course first, then add its features.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningAdmin;
